import { Grid, Typography, Card, CardHeader, CardContent, CircularProgress, Box, Link } from '@mui/material';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const StyledGridItem = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
    [theme.breakpoints.up('md')]: {
        width: '50%',
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
    height: '100%',
}));

const ScalarValue = styled(Typography, { shouldForwardProp: (prop) => prop !== '$muted' })(({ theme, $muted }) => ({
    fontSize: theme.typography.size.xxxl,
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: 1.1,
    color: $muted ? theme.palette.text.secondary : theme.palette.text.primary,
}));

const ScalarNote = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
}));

const ContentGridContainer = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(0),
}));

const VBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    alignItems: 'flex-end',
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'right',
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
}));

const InvoiceLink = styled(Link)({
    textDecoration: 'none',
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
});

const InfoSmallBox = (data) => {
    const { title, content, onClick, invoiceData, headerAction } = data;
    const navigate = useNavigate();
    const params = useParams();
    const count = Object.keys(content || {}).length;
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    useEffect(() => {
        if (title !== undefined && content !== undefined) {
            setIsContentLoaded(true);
        }
    }, [title, content]);

    const isScalar = isContentLoaded && count < 1;
    const scalarValue = typeof content === 'number' ? content : 0;

    if (isScalar) {
        const isZero = scalarValue === 0;
        const zeroNote = title ? `No ${title.replace(/^Open\s+/i, '').toLowerCase()}` : "None";
        return (
            <StyledGridItem item>
                <StyledCard onClick={onClick}>
                    <CardHeader title={title} action={headerAction} />
                    <CardContent>
                        <ScalarValue $muted={isZero}>{scalarValue}</ScalarValue>
                        {isZero && <ScalarNote>{zeroNote}</ScalarNote>}
                    </CardContent>
                </StyledCard>
            </StyledGridItem>
        );
    }

    let renderContent = null;
    renderContent = (<CircularProgress />);

    if (isContentLoaded) {
        if (count >= 1) {
            renderContent = (
                <Grid container spacing={1.5}>
                    {Object.keys(content || {}).map((keyName) => (
                        <ContentGridContainer key={keyName} item xs={12}>
                            <LabelTypography variant="body2">{keyName}</LabelTypography>
                            {Array.isArray(content[keyName]) ? (
                                <VBox>
                                    {content[keyName].length === 0 ? (
                                        <ValueTypography variant="body2" color="text.secondary">
                                            None
                                        </ValueTypography>
                                    ) : keyName === "Unpaid Invoices" && invoiceData && invoiceData.length > 0 ? (
                                        // Special handling for invoice numbers - make them clickable
                                        invoiceData.map((invoice, index) => {
                                            const clientSlug = params.clientSlug || window.location.pathname.split('/')[2];
                                            return (
                                                <InvoiceLink
                                                    key={index}
                                                    component="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (invoice.id && clientSlug) {
                                                            navigate(`/client/${clientSlug}/invoices/${invoice.id}`);
                                                        }
                                                    }}
                                                >
                                                    <ValueTypography variant="body2" color="primary">
                                                        {invoice.number}
                                                    </ValueTypography>
                                                </InvoiceLink>
                                            );
                                        })
                                    ) : (
                                        content[keyName].map((item, index) => (
                                            <ValueTypography key={index} variant="body2">
                                                {typeof item === 'object' && item.number ? item.number : item}
                                            </ValueTypography>
                                        ))
                                    )}
                                </VBox>
                            ) : (
                                <ValueTypography variant="body2">
                                    {content[keyName]}
                                </ValueTypography>
                            )}
                        </ContentGridContainer>
                    ))}
                </Grid>
            );
        }
    }

    return (
        <StyledGridItem item>
            <StyledCard onClick={onClick}>
                <CardHeader title={title} action={headerAction} />
                <CardContent>
                    {renderContent}
                </CardContent>
            </StyledCard>
        </StyledGridItem>
    );


};

export default InfoSmallBox;